import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { getSeriesColor } from './color-palette';
import { ChartPatternDefs } from './ChartPatternDefs';
import { ChartTooltip, TooltipData } from './ChartTooltip';

const MARGIN = { top: 20, right: 20, bottom: 60, left: 60 };
const WIDTH = 600;
const HEIGHT = 400;

/** Point shape symbols for accessibility — supplements color */
const POINT_SHAPES: d3.SymbolType[] = [
  d3.symbolCircle,
  d3.symbolSquare,
  d3.symbolTriangle,
  d3.symbolDiamond,
  d3.symbolStar,
  d3.symbolCross,
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash + char) | 0;
  }
  return Math.abs(hash);
}

/**
 * Line chart using D3.js for SVG rendering.
 * Data points use different shapes for accessibility.
 * Supports keyboard navigation and tooltips.
 */
export function LineChart({ data, dimensionLabel, measureLabel, onSegmentClick }: ChartProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const handlePointInteraction = useCallback(
    (dimensionValue: string, measureValue: number, event: React.MouseEvent | React.FocusEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const target = event.currentTarget as HTMLElement;
      const targetRect = target.getBoundingClientRect();
      setTooltip({
        dimensionValue,
        measureValue,
        dimensionLabel,
        measureLabel,
        x: targetRect.left - rect.left + targetRect.width / 2,
        y: targetRect.top - rect.top,
      });
    },
    [dimensionLabel, measureLabel],
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  useEffect(() => {
    if (!svgRef.current || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('g.chart-content').remove();

    const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
    const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg
      .append('g')
      .attr('class', 'chart-content')
      .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const xScale = d3
      .scalePoint()
      .domain(data.map((d) => d.dimensionValue))
      .range([0, innerWidth])
      .padding(0.5);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.measureValue) ?? 0])
      .nice()
      .range([innerHeight, 0]);

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .attr('transform', 'rotate(-30)')
      .style('text-anchor', 'end')
      .attr('font-size', '12px');

    // Y axis
    g.append('g').call(d3.axisLeft(yScale)).attr('font-size', '12px');

    // Axis labels
    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + MARGIN.bottom - 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('fill', 'currentColor')
      .text(dimensionLabel);

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -MARGIN.left + 15)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('fill', 'currentColor')
      .text(measureLabel);

    // Line
    const line = d3
      .line<{ dimensionValue: string; measureValue: number }>()
      .x((d) => xScale(d.dimensionValue) ?? 0)
      .y((d) => yScale(d.measureValue));

    g.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', 'hsl(var(--primary))')
      .attr('stroke-width', 2)
      .attr('d', line);

    // Points with different shapes for accessibility
    data.forEach((d) => {
      const color = getSeriesColor(d.dimensionValue);
      const shapeIndex = hashString(d.dimensionValue) % POINT_SHAPES.length;
      const shapeType = POINT_SHAPES[shapeIndex] ?? d3.symbolCircle;
      const symbol = d3.symbol().type(shapeType).size(80);

      g.append('path')
        .attr('d', symbol() ?? '')
        .attr('transform', `translate(${xScale(d.dimensionValue) ?? 0},${yScale(d.measureValue)})`)
        .attr('fill', color)
        .attr('stroke', 'white')
        .attr('stroke-width', 1);
    });
  }, [data, dimensionLabel, measureLabel]);

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground" role="status">
        No data available
      </div>
    );
  }

  const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

  const xScale = d3
    .scalePoint()
    .domain(data.map((d) => d.dimensionValue))
    .range([0, innerWidth])
    .padding(0.5);

  const yScale = d3
    .scaleLinear()
    .domain([0, d3.max(data, (d) => d.measureValue) ?? 0])
    .nice()
    .range([innerHeight, 0]);

  const POINT_SIZE = 24;

  return (
    <div ref={containerRef} className="relative" role="img" aria-label={`Line chart: ${dimensionLabel} by ${measureLabel}`}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-auto w-full max-w-[600px]"
        aria-hidden="true"
      >
        <ChartPatternDefs />
      </svg>
      {/* Keyboard-navigable point overlays */}
      <div className="absolute inset-0" aria-label="Chart data points" role="list">
        {data.map((d) => {
          const cx = (MARGIN.left + (xScale(d.dimensionValue) ?? 0)) / WIDTH * 100;
          const cy = (MARGIN.top + yScale(d.measureValue)) / HEIGHT * 100;
          const pointW = POINT_SIZE / WIDTH * 100;
          const pointH = POINT_SIZE / HEIGHT * 100;

          return (
            <button
              key={d.dimensionValue}
              type="button"
              role="listitem"
              className="absolute rounded-full border-0 bg-transparent p-0 outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              style={{
                left: `${cx - pointW / 2}%`,
                top: `${cy - pointH / 2}%`,
                width: `${pointW}%`,
                height: `${pointH}%`,
              }}
              tabIndex={0}
              aria-label={`${dimensionLabel}: ${d.dimensionValue}, ${measureLabel}: ${d.measureValue}`}
              onMouseEnter={(e) => handlePointInteraction(d.dimensionValue, d.measureValue, e)}
              onFocus={(e) => handlePointInteraction(d.dimensionValue, d.measureValue, e)}
              onMouseLeave={hideTooltip}
              onBlur={hideTooltip}
              onClick={() => onSegmentClick?.(d.dimensionValue)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSegmentClick?.(d.dimensionValue);
                }
              }}
            />
          );
        })}
      </div>
      <ChartTooltip tooltip={tooltip} />
    </div>
  );
}
